import { useState } from "react";
import { Navigation, Loader2 } from "lucide-react";
import { loadMaps } from "@/lib/geo";
import { PlaceAutocomplete } from "@/components/PlaceAutocomplete";

// Quick, indicative distance-based estimate for per-KM / per-KM+hour mobility listings.
// `compact` renders an inline mini version for cards; default is the full detail-page block
// with a live traffic-based fare preview from a pickup → drop search.
export function FareEstimator({ mob = {}, compact = false }) {
  const [km, setKm] = useState("");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trip, setTrip] = useState(null); // { km, mins }
  const rate = Number(mob.rate_km) || 0;
  const minKm = Number(mob.min_km) || 0;
  const hasHour = mob.pricing_model === "per_km_hour" && Number(mob.rate_hour) > 0;
  const eff = Math.max(Number(km) || 0, minKm);
  const est = eff > 0 ? Math.round(eff * rate) : 0;
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const computeTrip = async (f, t) => {
    if (!f?.lat || !t?.lat) return;
    setBusy(true);
    try {
      const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      await loadMaps(key);
      const g = window.google;
      const svc = new g.maps.DirectionsService();
      svc.route({ origin: { lat: f.lat, lng: f.lng }, destination: { lat: t.lat, lng: t.lng },
        travelMode: g.maps.TravelMode.DRIVING, drivingOptions: { departureTime: new Date(), trafficModel: g.maps.TrafficModel.BEST_GUESS } },
        (res, st) => {
          setBusy(false);
          if (st === "OK" && res) {
            const leg = res.routes?.[0]?.legs?.[0];
            const kmVal = (leg?.distance?.value || 0) / 1000;
            const mins = Math.round((leg?.duration_in_traffic?.value || leg?.duration?.value || 0) / 60);
            setKm(kmVal ? kmVal.toFixed(1) : "");
            setTrip({ km: kmVal.toFixed(1), mins });
          } else { setTrip(null); }
        });
    } catch { setBusy(false); }
  };
  const onFrom = (addr, coords) => { const v = coords?.lat ? { ...coords, addr } : null; setFrom(v); if (v && to) computeTrip(v, to); };
  const onTo = (addr, coords) => { const v = coords?.lat ? { ...coords, addr } : null; setTo(v); if (v && from) computeTrip(from, v); };

  if (compact) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5" data-testid="fare-estimator" onClick={stop}>
        <input type="number" min="0" value={km} onChange={(e) => setKm(e.target.value)} onClick={stop} placeholder="km"
          data-testid="fare-estimator-km" className="h-6 w-14 rounded border border-slate-200 px-1.5 text-[11px] outline-none focus:border-sky-400" />
        <span className="text-[11px] text-slate-400">× ₹{rate}/km</span>
        {(Number(km) || 0) > 0 && <span className="text-[11px] font-bold text-slate-700" data-testid="fare-estimator-result">≈ ₹{est}{hasHour ? "+hr" : ""}</span>}
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/60 p-3" data-testid="fare-estimator">
      <p className="text-xs font-semibold text-sky-800">Live fare preview</p>
      <p className="mt-0.5 text-[11px] text-slate-500">Enter your pickup &amp; drop for a real road distance and live‑traffic fare, or type the distance below.</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2" onClick={stop}>
        <PlaceAutocomplete testid="fare-from" placeholder="Pickup" onChange={onFrom} />
        <PlaceAutocomplete testid="fare-to" placeholder="Drop" onChange={onTo} />
      </div>
      {busy && <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking live traffic…</p>}
      {trip && !busy && (
        <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-2.5 py-1 text-[11px] font-bold text-white" data-testid="fare-trip">
          <Navigation className="h-3 w-3" /> ~{trip.km} km · ~{trip.mins} min in traffic
        </p>
      )}
      <div className="mt-2 flex items-center gap-2">
        <input type="number" min="0" value={km} onChange={(e) => { setKm(e.target.value); setTrip(null); }} placeholder="Distance (km)"
          data-testid="fare-estimator-km" className="h-8 w-32 rounded-md border border-slate-200 px-2 text-sm outline-none focus:border-sky-400" />
        <span className="text-sm text-slate-500">× ₹{rate}/km{minKm ? ` (min ${minKm} km)` : ""}</span>
      </div>
      {(Number(km) || 0) > 0 && <p className="mt-1.5 text-sm text-slate-700" data-testid="fare-estimator-result">≈ <span className="font-bold text-slate-900">₹{est}</span>{hasHour ? " + hourly" : ""} <span className="text-xs text-slate-400">· indicative; final fare confirmed by the provider</span></p>}
    </div>
  );
}
